import { useEffect, useRef } from 'react';
import { startCalendarReminderScheduler, stopCalendarReminderScheduler } from '../services/calendarReminderScheduler';
import { autoStartOnBoot as autoStartMailCatcher, bootMailBadge, useMailCatcherStore } from '../store/mailCatcherStore';
import { useMockServerStore } from '../store/mockServerStore';
import { stopMockServer } from '../services/mockServerService';
import { useMockServerSync } from './useMockServerSync';

/**
 * The app's BOOT services, run only while someone is signed in and past the
 * desktop sign-in gate (owner, 26.10.2026: "no app behaviour without
 * sign-in"):
 *
 *   · calendar reminders (they fire desktop notifications from the open
 *     database — signed out that is the `_local` one);
 *   · the mail catcher: the badge subscription and the SMTP auto-start;
 *   · the mock-server sync (reloads the local mock server from collections).
 *
 * `active` false → none of them starts. active → false (sign-out, the gate
 * coming back, an expired plan claim) → the reminder scan stops, a pending
 * mail-catcher auto-start is cancelled, and a running SMTP listener and mock
 * server are stopped: they serve the signed-out account's data otherwise.
 *
 * Mounted in App with the same readiness as content deep links:
 * `isAuthenticated && desktopGateState(…) === 'app'`.
 */
export function useSignedInServices({ active }) {
  useMockServerSync({ active });

  // Calendar reminders: start is idempotent and returns the stop fn.
  useEffect(() => {
    if (!active) return undefined;
    return startCalendarReminderScheduler();
  }, [active]);

  // Mail catcher: badge + auto-start (waits for the preferences to hydrate).
  useEffect(() => {
    if (!active) return undefined;
    bootMailBadge();
    return autoStartMailCatcher();
  }, [active]);

  // Leaving the signed-in state stops what is already running.
  const wasActive = useRef(false);
  useEffect(() => {
    if (active) {
      wasActive.current = true;
      return;
    }
    if (!wasActive.current) return;
    wasActive.current = false;
    stopCalendarReminderScheduler();
    const mail = useMailCatcherStore.getState();
    if (mail.status?.running || mail.starting) mail.stop();
    if (useMockServerStore.getState().running) {
      stopMockServer()
        .catch(() => {})
        .finally(() => useMockServerStore.getState().reset());
    }
  }, [active]);
}
